import { ArrowDown, ArrowUp, Shuffle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { QuizTeacher } from "@/modes/quiz/QuizTeacher";
import { QuizStudent } from "@/modes/quiz/QuizStudent";
import { QuizSettings } from "@/modes/quiz/QuizSettings";
import { InfoTip, SettingSelect, SettingSwitch } from "@/components/game/SettingFields";
import { Input } from "@/components/ui/input";
import { pointColor } from "@/modes/wheel/WheelBoard";

const STATUS = { played: "text-slate-500 line-through", skipped: "text-amber-400", new: "" };
const SPECIAL_LABEL = { iflas: "İFLAS", pas: "PAS" };
const sliceLabel = (v) => SPECIAL_LABEL[v] || `${v} puan`;
const teamName = (state, id) => state.teams.find((t) => t.id === id);

export const WheelPlan = ({ state, send }) => (
  <div className="panel p-5 space-y-4" data-testid="wheel-plan">
    <h3 className="font-display text-xl font-bold">Çark planı</h3>
    <p className="text-sm text-slate-400">Çark bir puana denk gelince o seviyenin sıradaki kullanılmamış sorusu açılır.</p>
    {state.wheel_plan?.map((lvl) => (
      <div key={lvl.level} className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="font-display text-lg font-bold text-[#FACC15]">{lvl.level} puan</span>
          <Button size="sm" variant="ghost" onClick={() => send({ type: "cmd", action: "plan_shuffle", level: lvl.level })} data-testid={`plan-shuffle-${lvl.level}`}><Shuffle className="h-4 w-4 mr-1" />Karıştır</Button>
        </div>
        {lvl.questions.map((q, i) => (
          <div key={q.id} className="flex items-center gap-2 rounded-xl bg-white/5 px-3 py-1.5 text-sm" data-testid={`plan-item-${q.id}`}>
            <span className="text-slate-500 w-5">{i + 1}.</span>
            <span className={`flex-1 ${STATUS[q.status]}`}>{q.text}{q.status === "skipped" ? " (atlandı)" : ""}</span>
            <button onClick={() => send({ type: "cmd", action: "plan_move", level: lvl.level, question_id: q.id, dir: "up" })} aria-label="Yukarı" data-testid={`plan-up-${q.id}`}><ArrowUp className="h-4 w-4" /></button>
            <button onClick={() => send({ type: "cmd", action: "plan_move", level: lvl.level, question_id: q.id, dir: "down" })} aria-label="Aşağı" data-testid={`plan-down-${q.id}`}><ArrowDown className="h-4 w-4" /></button>
          </div>
        ))}
      </div>
    ))}
  </div>
);

export const WheelTeacher = ({ state, send }) => {
  const next = teamName(state, state.next_team);
  const spin = state.spin;
  return (
    <div className="space-y-6">
      {state.phase === "wheel" && (
        <div className="panel p-5 space-y-1" data-testid="wheel-teacher-status">
          <p className="font-display text-2xl font-bold">{spin?.pending ? "Çark dönüyor…" : spin ? `Çark: ${sliceLabel(spin.value)}` : "Çarkı çevirin"}</p>
          {next && !spin?.pending && <p className="text-slate-300">Sıradaki takım: <b style={{ color: next.color }}>{next.name}</b></p>}
        </div>
      )}
      <QuizTeacher state={state} send={send} />
      <WheelPlan state={state} send={send} />
    </div>
  );
};

export const WheelStudent = ({ state, send, remaining }) => {
  const turn = teamName(state, state.spin?.turn_team) || (state.spin?.turn_name ? { name: state.spin.turn_name, color: "#FACC15" } : null);
  const solo = state.solo;
  if (state.phase === "wheel") {
    const mine = state.my_team_turn && state.spin?.pending;
    return (
      <div className="py-10 text-center space-y-4" data-testid="student-wheel">
        <p className="font-display text-4xl font-bold text-[#FACC15]">ÇARKIFELEK</p>
        {state.spin && !state.spin.pending ? (
          <p className="text-xl">{state.spin.value === "iflas" ? `İFLAS! ${turn?.name || ""} puanı sıfırlandı.` : state.spin.value === "pas" ? "PAS! Sıra sonraki takımda." : `${state.spin.value} puanlık soru geliyor!`}</p>
        ) : <p className="text-xl">{solo ? "Çark rastgele bir öğrenci seçecek. Tahtaya bak!" : mine ? "Takımın çarkı çeviriyor, tahtaya bak!" : "Sıra gelen takım tahtada çarkı çevirecek."}</p>}
      </div>
    );
  }
  if (state.phase === "question") {
    return (
      <div className="py-12 text-center space-y-3" data-testid="student-not-turn">
        {turn && <p className="font-display text-3xl font-bold" style={{ color: turn.color }}>{turn.name}</p>}
        <p className="text-xl text-slate-300">
          {solo ? (state.my_team_turn ? "Sıra sende! Tahtaya gel ve soruyu tahtada yanıtla." : "Arkadaşın tahtada yanıtlıyor. Tahtadan izle!")
            : state.my_team_turn ? "Tahtadaki takım arkadaşın yanıtlıyor. Ona destek ol!" : "Bu takım tahtada yanıtlıyor. Tahtadan izle!"}
        </p>
      </div>
    );
  }
  return <QuizStudent state={state} send={send} remaining={remaining} />;
};

const parseSlices = (text) => text.split(/[,;\s]+/).map((s) => Number(s.trim())).filter((n) => Number.isInteger(n) && n > 0);
const COUNT = [0, 1, 2, 3, 4].map((n) => [n, String(n)]);

const SliceColors = ({ settings, onChange }) => {
  const levels = [...new Set((settings.slices || []).map(Number))].sort((x, y) => x - y);
  const set = (v, c) => onChange({ slice_colors: { ...(settings.slice_colors || {}), [String(v)]: c } });
  return (
    <div className="sm:col-span-2 space-y-1.5" data-testid="setting-slice-colors">
      <p className="text-sm text-slate-300">Puan renkleri</p>
      <div className="flex flex-wrap gap-2">
        {levels.map((v) => (
          <label key={v} className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 pl-1.5 pr-3 py-1.5 cursor-pointer">
            <input type="color" value={pointColor(v, settings)} onChange={(e) => set(v, e.target.value.toUpperCase())}
              className="h-8 w-8 rounded-lg bg-transparent border-0 cursor-pointer" data-testid={`setting-slice-color-${v}`} />
            <span className="font-display font-semibold">{v} puan</span>
          </label>
        ))}
      </div>
    </div>
  );
};

export const WheelSettings = ({ settings, onChange }) => (
  <>
    <p className="sm:col-span-2 text-sm text-slate-300" data-testid="wheel-board-answer-note">Sıradaki takımdan bir öğrenci tahtaya gelir, çarkı çevirir ve çıkan soruyu tahtada seçerek yanıtlar. Sorusu kalmayan puan çarktan otomatik düşer.</p>
    <SettingSwitch label="Soru süresini kapat" info="Açılırsa soruya süre sınırı olmaz; tahtadaki öğrenci istediği kadar düşünüp yanıtlar." checked={Boolean(settings.no_timer)} onChange={(v) => onChange({ no_timer: v })} testId="setting-no-timer" />
    <SettingSelect label="İflas dilimi sayısı" info="Çarkta kaç İFLAS dilimi olsun. İflas gelirse sıradaki takımın (ya da öğrencinin) puanı sıfırlanır." value={settings.bankrupt_count ?? 1} testId="setting-bankrupt-count" onChange={(v) => onChange({ bankrupt_count: Number(v) })} options={COUNT} />
    <SettingSelect label="Pas dilimi sayısı" info="Çarkta kaç PAS dilimi olsun. Pas gelirse soru gelmez, sıra sonrakine geçer." value={settings.pass_count ?? 1} testId="setting-pass-count" onChange={(v) => onChange({ pass_count: Number(v) })} options={COUNT} />
    <SettingSelect label="Çark sesi" info="Çark dönerken tahtada çark efekti ve sesi çalınsın mı." value={settings.jingle === "off" ? "off" : "internal"} testId="setting-jingle" onChange={(v) => onChange({ jingle: v })}
      options={[["internal", "Açık"], ["off", "Kapalı"]]} />
    <div className="sm:col-span-2 space-y-2">
      <div className="flex items-center gap-1.5"><p className="text-sm text-slate-300">Puan dilimleri</p><InfoTip text="Çarktaki puan değerlerini virgülle yazın (örn. 10, 20, 30, 40). Her değer için havuzdan o puandaki sorular gelir; sorusu kalmayan puan çarktan düşer." testId="setting-slices-info" /></div>
      <Input key={(settings.slices || []).join(",")} defaultValue={(settings.slices || []).join(", ")} className="h-11" data-testid="setting-slices"
        onBlur={(e) => onChange({ slices: parseSlices(e.target.value) })} />
    </div>
    <SliceColors settings={settings} onChange={onChange} />
    <QuizSettings settings={settings} onChange={onChange} hideScoring />
  </>
);
